import React from 'react';
import CreditCardFields from '../components/CreditCardFields';
import CreditCardPayerFields from '../components/CreditCardPayerFields';
import PaymentMethodSelector from '../components/PaymentMethodSelector';
import StripePaymentElement from '../components/StripePaymentElement';
import type { CheckoutCardData, CheckoutPayerData, PaymentMethod } from '../checkout.types';
import styles from '../components/CheckoutShared.module.css';

interface PaymentStepProps {
  paymentMethod: PaymentMethod | null;
  card: CheckoutCardData;
  payer: CheckoutPayerData;
  useStudentDataAsPayer: boolean;
  paymentError?: string;
  cardErrors?: Record<string, string>;
  disabled?: boolean;
  /** Checkout resumido: sem checkbox "usar dados da aluna". */
  chargeCheckoutMode?: boolean;
  internationalCheckout?: boolean;
  stripeClientSecret?: string | null;
  stripePaymentReady?: boolean;
  onStripeSubmittingChange?: (submitting: boolean) => void;
  onStripeError?: (message: string) => void;
  onStripeLoadFailed?: () => void;
  onStripeReady?: () => void;
  onStripeConfirmed?: () => void;
  onPaymentMethodChange: (method: PaymentMethod) => void;
  onCardFieldChange: <K extends keyof CheckoutCardData>(
    field: K,
    value: CheckoutCardData[K],
  ) => void;
  onPayerFieldChange: <K extends keyof CheckoutPayerData>(
    field: K,
    value: CheckoutPayerData[K],
  ) => void;
  onUseStudentDataAsPayerChange: (useStudentData: boolean) => void;
}

const PaymentStep: React.FC<PaymentStepProps> = ({
  paymentMethod,
  card,
  payer,
  useStudentDataAsPayer,
  paymentError,
  cardErrors = {},
  disabled = false,
  chargeCheckoutMode = false,
  internationalCheckout = false,
  stripeClientSecret = null,
  stripePaymentReady = false,
  onStripeSubmittingChange = () => undefined,
  onStripeError = () => undefined,
  onStripeLoadFailed = () => undefined,
  onStripeReady = () => undefined,
  onStripeConfirmed = () => undefined,
  onPaymentMethodChange,
  onCardFieldChange,
  onPayerFieldChange,
  onUseStudentDataAsPayerChange,
}) => (
  <div className={styles.paymentFields}>
    {internationalCheckout ? (
      <>
        {stripeClientSecret ? (
          <StripePaymentElement
            clientSecret={stripeClientSecret}
            disabled={disabled}
            onSubmittingChange={onStripeSubmittingChange}
            onError={onStripeError}
            onLoadFailed={onStripeLoadFailed}
            onReady={onStripeReady}
            onConfirmed={onStripeConfirmed}
          />
        ) : null}
        {!stripePaymentReady && (
          <p className={styles.stripeStatus}>
            O pagamento será processado em BRL pelo Stripe. Continue para abrir o ambiente seguro.
          </p>
        )}
      </>
    ) : (
      <>
        <PaymentMethodSelector
          value={paymentMethod}
          error={paymentError}
          disabled={disabled}
          onChange={onPaymentMethodChange}
        />

        {paymentMethod === 'credit_card' && (
          <>
            <CreditCardFields
              data={card}
              errors={cardErrors}
              disabled={disabled}
              onFieldChange={onCardFieldChange}
            />
            <CreditCardPayerFields
              data={payer}
              useStudentDataAsPayer={useStudentDataAsPayer}
              hideUseStudentDataToggle={chargeCheckoutMode}
              errors={cardErrors}
              disabled={disabled}
              onFieldChange={onPayerFieldChange}
              onUseStudentDataChange={onUseStudentDataAsPayerChange}
            />
          </>
        )}
      </>
    )}
  </div>
);

export default PaymentStep;
